// components/copilot/hooks/useDrawerResize.js
import { useEffect, useRef, useState } from "react";

export function useDrawerResize({ storageKey, defaultWidth, clampWidth, open, isFullscreen }) {
  const [drawerWidth, setDrawerWidth] = useState(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      const parsed = raw != null ? Number(raw) : NaN;
      if (!Number.isNaN(parsed)) return clampWidth(parsed);
    } catch {}
    return clampWidth(defaultWidth);
  });

  const [isResizing, setIsResizing] = useState(false);
  const prevWidthRef = useRef(drawerWidth);

  const onStartResize = (e) => {
    if (isFullscreen) return;
    e.preventDefault();
    setIsResizing(true);
  };

  // keep width within bounds on viewport resize
  useEffect(() => {
    const onResize = () => {
      if (isFullscreen) return;
      setDrawerWidth((prev) => clampWidth(prev));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isFullscreen, clampWidth]);

  useEffect(() => {
    if (!isResizing) return;

    const onMove = (e) => {
      const clientX = e?.clientX ?? (e?.touches?.[0]?.clientX ?? 0);
      const nextWidth = clampWidth(window.innerWidth - clientX);
      setDrawerWidth(nextWidth);
    };

    const onUp = () => {
      setIsResizing(false);
      try {
        window.localStorage.setItem(storageKey, String(drawerWidth));
      } catch {}
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [isResizing, drawerWidth, clampWidth, storageKey]);

  // when drawer closes, reset local resize state
  useEffect(() => {
    if (open) return;
    setIsResizing(false);
  }, [open]);

  useEffect(() => {
    if (isFullscreen) return;
    prevWidthRef.current = drawerWidth;
  }, [drawerWidth, isFullscreen]);

  return { drawerWidth, setDrawerWidth, isResizing, onStartResize, prevWidthRef };
}
